/**
 * Stichwort-Erkennung für das Kontaktformular (#1071).
 *
 * Die Erkennung läuft ausschliesslich im Browser des Nutzers: kein Netzaufruf,
 * kein Tracking, keine Auswertung des Nachrichteninhalts. Sie blendet nur einen
 * Hinweis auf eine passende FAQ-Frage ein und blockiert das Absenden nie.
 *
 * Diese Datei enthält bewusst nur die Mechanik. Die Regeln selbst stehen in
 * `faq-hint.rules.ts`.
 */

/** Ein Stichwort als Wortphrase oder — für Sonderfälle — als eigener Ausdruck. */
export type HintPattern = string | RegExp;

/** Ein Stichwort, das nur unter zusätzlichen Bedingungen zählt. */
export interface HintTrigger {
  /** Das Stichwort selbst. */
  pattern: HintPattern;
  /** Mindestens eines davon muss ebenfalls in der Nachricht stehen. */
  requiresAny?: readonly HintPattern[];
  /** Keines davon darf in der Nachricht stehen. */
  forbidsAny?: readonly HintPattern[];
}

export interface FaqHintRule {
  /** Stabile Kennung der Regel — auch der Schlüssel fürs Wegklicken. */
  id: string;
  /** FAQ-Fragen, auf die der Hinweis verweist (`/faq#<id>`). */
  faqIds: readonly string[];
  /** Höhere Zahl gewinnt, wenn mehr Regeln passen als Karten erlaubt sind. */
  priority: number;
  /** Eines dieser Stichwörter muss passen, damit die Regel greift. */
  patterns: readonly (HintPattern | HintTrigger)[];
  /** Steht eines davon in der Nachricht, greift die Regel nicht. */
  excludes?: readonly HintPattern[];
}

/** Kürzere Nachrichten werden nicht geprüft — sonst blitzt beim Tippen ständig etwas auf. */
export const MIN_MESSAGE_LENGTH = 15;

/** Mehr als zwei Hinweise gleichzeitig sind Rauschen. */
export const MAX_FAQ_HINTS = 2;

/** Bis zu dieser Länge gilt eine Nachricht als „ohne Anliegen". */
export const CALLBACK_HINT_MAX_LENGTH = 40;

/**
 * Vereinheitlicht die Nachricht für den Vergleich: Kleinschreibung,
 * Umlaute als ae/oe/ue, ß als ss, Bindestriche als Leerzeichen („Magen-Darm“
 * trifft das Stichwort „magen darm“), Mehrfach-Leerraum zusammengezogen.
 */
export function normalizeForMatching(text: string): string {
  return text
    .normalize('NFC')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Grussformeln, in denen ein Tageswort steckt, ohne einen Tag zu meinen. */
const GREETINGS =
  /\b(?:einen |schoenen )?(?:guten|schoenen) (?:morgen|tag|abend)\b/g;

/**
 * Entfernt Grussformeln aus der bereits normalisierten Nachricht.
 *
 * Ohne diesen Schritt macht „Guten Morgen" aus jeder Vertragsfrage einen
 * Tagesbezug — und „Guten Morgen, ich möchte zum 31.07. abmelden" landete beim
 * Hinweis für die Betreuung vor Ort statt bei der Kündigung.
 */
export function stripGreetings(normalizedMessage: string): string {
  return normalizedMessage.replace(GREETINGS, ' ').replace(/\s+/g, ' ').trim();
}

const regExpCache = new Map<string, RegExp>();

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Baut aus einer Wortphrase einen Ausdruck, der auf Wortgrenzen matcht —
 * damit „Krankenkasse" nicht das Stichwort „krank" auslöst.
 */
function toRegExp(pattern: HintPattern): RegExp {
  if (pattern instanceof RegExp) {
    return pattern;
  }
  const normalized = normalizeForMatching(pattern);
  const cached = regExpCache.get(normalized);
  if (cached) {
    return cached;
  }
  const body = escapeRegExp(normalized).replace(/\\?\s+/g, '\\s+');
  const compiled = new RegExp(`\\b${body}\\b`);
  regExpCache.set(normalized, compiled);
  return compiled;
}

/** Prüft ein einzelnes Stichwort gegen eine bereits normalisierte Nachricht. */
export function matchesPattern(
  normalizedMessage: string,
  pattern: HintPattern
): boolean {
  return toRegExp(pattern).test(normalizedMessage);
}

function matchesAny(
  normalizedMessage: string,
  patterns: readonly HintPattern[] | undefined
): boolean {
  return (patterns ?? []).some((pattern) =>
    matchesPattern(normalizedMessage, pattern)
  );
}

function isTrigger(
  candidate: HintPattern | HintTrigger
): candidate is HintTrigger {
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    !(candidate instanceof RegExp)
  );
}

function triggerMatches(
  normalizedMessage: string,
  candidate: HintPattern | HintTrigger
): boolean {
  if (!isTrigger(candidate)) {
    return matchesPattern(normalizedMessage, candidate);
  }
  if (!matchesPattern(normalizedMessage, candidate.pattern)) {
    return false;
  }
  if (
    candidate.requiresAny?.length &&
    !matchesAny(normalizedMessage, candidate.requiresAny)
  ) {
    return false;
  }
  if (matchesAny(normalizedMessage, candidate.forbidsAny)) {
    return false;
  }
  return true;
}

/** Prüft eine einzelne Regel gegen eine bereits normalisierte Nachricht. */
export function ruleMatches(
  normalizedMessage: string,
  rule: FaqHintRule
): boolean {
  if (matchesAny(normalizedMessage, rule.excludes)) {
    return false;
  }
  return rule.patterns.some((candidate) =>
    triggerMatches(normalizedMessage, candidate)
  );
}

/**
 * Liefert die passenden Regeln, nach Priorität sortiert und auf
 * {@link MAX_FAQ_HINTS} gedeckelt. Kurze Nachrichten ergeben immer eine leere
 * Liste.
 */
export function matchFaqHintRules(
  message: string,
  rules: readonly FaqHintRule[]
): FaqHintRule[] {
  const trimmed = message?.trim() ?? '';
  if (trimmed.length < MIN_MESSAGE_LENGTH) {
    return [];
  }
  const normalized = stripGreetings(normalizeForMatching(trimmed));
  return rules
    .filter((rule) => ruleMatches(normalized, rule))
    .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id))
    .slice(0, MAX_FAQ_HINTS);
}

/**
 * Erkennt reine Rückruf-Bitten ohne Anliegen („Bitte rufen Sie mich zurück").
 * Bewusst nur bei kurzen Nachrichten — wer sein Anliegen beschreibt und
 * nebenbei um einen Rückruf bittet, braucht den Hinweis nicht.
 */
export function matchesCallbackWithoutDetails(
  message: string,
  patterns: readonly HintPattern[]
): boolean {
  const trimmed = message?.trim() ?? '';
  if (trimmed.length === 0 || trimmed.length > CALLBACK_HINT_MAX_LENGTH) {
    return false;
  }
  return matchesAny(stripGreetings(normalizeForMatching(trimmed)), patterns);
}
